import { Avatar, Loading, Button } from '@appkit4/react-components';
import { Badge } from '@appkit4/react-components/badge';
import clsx from 'clsx';
import { generateTimestamp } from '@utils/generateTimestamp';
import { useEffect, useRef, useContext, useMemo } from 'react';
import styles from './Message.module.scss';
import { SessionContext } from '@contexts/SessionProvider';
import { tryToCompleteFinalJSON } from '@utils/index';

interface iMessage {
    avatarName?: string;
    author: string;
    timestamp: number;
    children?: React.ReactNode;
    file?: File | null;
    blobUrl?: string | null;
    type: string;
    streamMessagesPool?: string[];
    mode: 'normal' | 'stream';
}
const STREAM_SPEED = 200;
const CHUNK_SIZE = 64;
const Message = ({ avatarName, children, author, timestamp, file, type, streamMessagesPool, mode }: iMessage) => {
    const { streamMessagesConsumer, setStreamMessagesConsumer, setShowCodeStream, showCodeStream } =
        useContext(SessionContext);
    //This state controlls the loading state of streaming message. When the streamMessagesPool is empty, it will be set false.
    const isCurrentMessageStreamingFinished = useRef(false);
    const messageIndex = useRef(0);
    const charIndex = useRef(0);
    const streamMessagesRenderer = (
        streamMessages: string[],
        isCurrentMessageStreamingFinished: boolean,
        isHistory: boolean
    ) => {
        return (
            <div>
                {streamMessages
                    .map((message, index) => {
                        if (!message) return '';
                        const splitResult = message?.split(':') || [];
                        const [messageType = '', ...messageContent] = splitResult;
                        const content = messageContent.join(':').trim();
                        switch (messageType) {
                            case 'instructions':
                                return content ? (
                                    <div key={index} className={styles.instructions}>
                                        {content}
                                    </div>
                                ) : (
                                    ''
                                );
                            default:
                                return '';
                        }
                    })
                    .filter((item) => item !== '')}
                {fileLoadingRenderer(streamMessages, isCurrentMessageStreamingFinished, isHistory)}
            </div>
        );
    };

    const fileLoadingRenderer = (
        streamMessages: string[],
        isCurrentMessageStreamingFinished: boolean,
        isHistory: boolean
    ) => {
        const showFileLoading = !isHistory && !isCurrentMessageStreamingFinished;

        if (streamMessages.length < 3 || !showFileLoading) return '';
        const currentFilesIndex = streamMessages.length - 3;
        const currentFilesInstructionIndex = streamMessages.length - 2;
        const fileContent = streamMessages[currentFilesIndex];
        const fileSplitResult = fileContent?.split(':') || [];
        const [messageType = '', ...fileName] = fileSplitResult;
        const content = fileName.join(':').trim();
        const fileInstructionContent = streamMessages[currentFilesInstructionIndex];
        const instructionSplitResult = fileInstructionContent?.split(':') || [];
        const [fileInstructionType = '', ...instructions] = instructionSplitResult;
        const instructionsContent = instructions.join(':').trim();

        return showFileLoading ? (
            <div>
                <Button
                    kind="secondary"
                    style={{ display: 'inline-block', minWidth: '70%' }}
                    className={styles.file}
                    size={'lg'}>
                    {messageType === 'file' && showFileLoading ? content : 'File generating...'}
                </Button>
                <Loading
                    style={{ display: 'inline-block', marginLeft: 8, marginTop: 8 }}
                    indeterminate
                    loadingType="circular"
                />
                <div className={styles.taskInstruction}>
                    {fileInstructionType === 'taskInstruction' ? instructionsContent : ''}
                </div>
            </div>
        ) : (
            ''
        );
    };

    const finalJSONparser = (finalObj: any) => {
        const finalMessages = [];

        if (finalObj.success) {
            finalMessages.push(`instructions:${finalObj.completion.message}`);
        }
        return finalMessages;
    };
    const normalMessageRenderer = (message?: string) => {
        if (!message) return '';
        try {
            const parsedMessage = tryToCompleteFinalJSON(message);
            if (!parsedMessage) return message;
            const nextMessage = finalJSONparser(parsedMessage);
            if (nextMessage) {
                return streamMessagesRenderer(nextMessage, true, true);
            }
            return message;
        } catch (err) {
            console.error('Error parsing message:', err);
            return message;
        }
    };
    //Comsume message from streamMessagesPool
    useEffect(() => {
        if (streamMessagesPool && streamMessagesPool.length > 0 && !showCodeStream) {
            setShowCodeStream(true);
        }
        isCurrentMessageStreamingFinished.current = false;
        try {
            if (streamMessagesPool && streamMessagesPool[0]) {
                const interval = setInterval(() => {
                    setStreamMessagesConsumer((prev: string[]) => {
                        const newMessages = [...prev];
                        if (streamMessagesPool[messageIndex.current]) {
                            if (charIndex.current < streamMessagesPool[messageIndex.current].length) {
                                if (!newMessages[messageIndex.current]) {
                                    newMessages[messageIndex.current] = '';
                                }
                                newMessages[messageIndex.current] += streamMessagesPool[messageIndex.current].slice(
                                    charIndex.current,
                                    charIndex.current + CHUNK_SIZE
                                );
                                charIndex.current += CHUNK_SIZE;
                            } else if (messageIndex.current < streamMessagesPool.length - 1) {
                                messageIndex.current += 1;
                                charIndex.current = 0;
                            } else {
                                isCurrentMessageStreamingFinished.current = true;
                                clearInterval(interval);
                            }
                        }
                        return newMessages;
                    });
                }, STREAM_SPEED);
                return () => {
                    clearInterval(interval);
                };
            }
        } catch (err) {
            console.error('Error in streaming message effect:', err);
        }
    }, [streamMessagesPool, showCodeStream]); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: setShowCodeStream and setStreamMessagesConsumer are stable setter functions from context

    const renderedMessages = useMemo(() => {
        return streamMessagesRenderer(streamMessagesConsumer, isCurrentMessageStreamingFinished.current, false);
    }, [JSON.stringify(streamMessagesConsumer)]); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: streamMessagesRenderer is intentionally omitted; JSON.stringify is used for memoization of array contents
    return (
        <div className={styles.message}>
            <div className={styles.content}>
                <div>
                    {type === 'question' ? (
                        <Avatar compact label={avatarName ? avatarName : 'AA'} />
                    ) : (
                        <div className="ask-appkit-avatar"></div>
                    )}
                </div>
                <div>
                    <div className={clsx(styles.wrapper, { ['wrapper-ask-appkit']: type === 'AI' })}>
                        {file && (
                            <Badge
                                className={styles.badge}
                                value="File"
                                type="primary-outlined"
                                icon="icon-document-file-outline"
                            />
                        )}
                        <div className={styles.text}>
                            {mode === 'normal' ? normalMessageRenderer(children?.toString()) : renderedMessages}
                            {file && <span className="filename">{file.name}</span>}
                        </div>
                    </div>
                    <p className={styles.author}>
                        {generateTimestamp(timestamp)} • {author}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Message;
